import type { GetRenderElementComponentType } from '../../../utils/getRenderElement';
import type { ParagraphPluginElement, ParagraphPluginPropertyOptions } from '../types';
import { PARAGRAPH_LIST_ITEM_VARIANT, PARAGRAPH_PLUGIN_ID } from '../types';
import styled, { css } from 'styled-components';
import { useCurrentUser } from 'hooks/useCurrentUser';
import type { Styles } from 'generated/graphql';
import { useReporterStyles } from 'hooks/useReporterStyles';
import { telemetryContextFor } from 'modules/Apollo/types';

type ParagraphWrapperProps = {
  shouldForceInline?: boolean;
  listItemVariant?: boolean;
  children: React.ReactNode;
};

export const ParagraphWrapper = ({
  children,
  shouldForceInline = false,
  listItemVariant = false,
}: ParagraphWrapperProps): React.ReactElement => {
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;

  const style: Styles = useReporterStyles(reporterSettings);

  const paragraphCss = css`
    margin: 0;
    display: ${listItemVariant ? undefined : shouldForceInline === true ? 'inline' : 'block'};
    text-transform: ${style?.bodyStyle?.textTransform};
    font-size: ${style?.bodyStyle?.fontSize}px;
    font-family: ${style?.bodyStyle?.fontFamily};
    font-weight: ${style?.bodyStyle?.textDecoration?.isBold === true ? 'bold' : 'normal'};
    font-style: ${style?.bodyStyle?.textDecoration?.isItalic === true ? 'italic' : 'normal'};
    text-decoration: ${style?.bodyStyle?.textDecoration?.isUnderline === true
      ? 'underline'
      : 'none'};
  `;

  if (listItemVariant === true) {
    return <li css={paragraphCss}>{children}</li>;
  }

  /**
   * Using a <div> instead of a <p> to allow nested UGC:
   * https://sironamedical.atlassian.net/browse/RP-1622
   */
  return (
    <div data-element-type={PARAGRAPH_PLUGIN_ID} role="paragraph" css={paragraphCss}>
      {children}
    </div>
  );
};

export const ParagraphEditable: GetRenderElementComponentType<
  ParagraphPluginPropertyOptions,
  ParagraphPluginElement
> = ({ element, children, ...rest }): React.ReactElement => (
  <ParagraphWrapper
    listItemVariant={element.variant === PARAGRAPH_LIST_ITEM_VARIANT}
    shouldForceInline={element.shouldForceInline}
  >
    {children}
  </ParagraphWrapper>
);

export const ParagraphRenderer = styled(
  ({ element, children }: { element: ParagraphPluginElement; children: React.ReactNode }) => (
    <ParagraphWrapper
      listItemVariant={element.variant === PARAGRAPH_LIST_ITEM_VARIANT}
      shouldForceInline={element.shouldForceInline}
    >
      {children}
    </ParagraphWrapper>
  )
)`
  white-space: pre-wrap;
`;

ParagraphWrapper.displayName = 'ParagraphWrapper';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ParagraphWrapper);
